var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { newrelic, runGoogleReCaptcha } from "@jti/fe-clients";
import { __, getConfig, isCustomErrorCheck } from "@jti/fe-common";
import { getIsEvo } from "../utils";
import { ConnectionError } from "../utils/ConnectionError";
import { RestLocalCache } from "./RestLocalCache";
const reCaptchaTokenKey = "X-DES-RECAPTCHA-TOKEN";
const reCaptchaFeatureToggle = "reCaptchaEnabled";
const reCaptchaId = "X-RECAPTCHA-ID";
export class Rest {
    constructor(baseUrl = "/") {
        this.name = "RestApi";
        this.isEvo = false;
        this.commonHeaders = {
            "content-type": "application/json",
        };
        this.baseUrl = baseUrl;
        this.config = {
            method: "GET",
        };
        this.cacheInstance = RestLocalCache.getInstance();
        this.isEvo = getIsEvo(baseUrl);
        this.initLocale();
    }
    setCommonHeader(key, value) {
        this.commonHeaders[key] = value;
    }
    /**
     * FIXME: at this moment the AbortSignal.timeout() is not fully supported by all of the green-browsers
     *  and current typescript version used in project, so we need to use "the ogly way" instead;
     * mr. FutureDeveloper, please fix this when you have time
     *
     * @param timeout in milliseconds
     * @returns AbortSignal
     */
    getTimeoutSignal(timeout) {
        const controller = new AbortController();
        setTimeout(() => controller.abort(), timeout || 5000);
        return controller.signal;
    }
    getConfigKeyValue(config, toggleKey) {
        const value = config[toggleKey];
        if (typeof value === "boolean") {
            return value;
        }
        return value === "true";
    }
    request(url, init = {}, useRecaptcha, useLocalCache, timeout) {
        return __awaiter(this, void 0, void 0, function* () {
            const currentUrl = `${this.baseUrl}${url}`;
            const isLocalCacheEnabled = useLocalCache && typeof window !== "undefined";
            const promiseCallback = (resolve, reject) => {
                const config = getConfig();
                const hasReCaptcha = this.getConfigKeyValue(config, reCaptchaFeatureToggle) && typeof window.grecaptcha !== "undefined";
                const token = config[reCaptchaId];
                const fetchCall = (tokenString = "") => {
                    if (hasReCaptcha && useRecaptcha) {
                        this.setCommonHeader(reCaptchaTokenKey, tokenString);
                    }
                    const headers = Object.assign(Object.assign({}, this.commonHeaders), init.headers);
                    if (timeout) {
                        this.config.signal = this.getTimeoutSignal(timeout);
                    }
                    fetch(currentUrl, Object.assign(Object.assign(Object.assign({}, this.config), init), { headers }))
                        .then((response) => __awaiter(this, void 0, void 0, function* () {
                        if (response.status === 204)
                            return resolve({ statusCode: response.status });
                        const data = yield response.json();
                        const responseData = Array.isArray(data)
                            ? { data, statusCode: response.status }
                            : Object.assign(Object.assign({}, data), { statusCode: response.status });
                        if (response.status >= 200 && response.status < 300) {
                            if (isLocalCacheEnabled) {
                                this.cacheInstance.setValue(currentUrl, responseData);
                            }
                            return resolve(responseData);
                        }
                        if (isLocalCacheEnabled) {
                            this.cacheInstance.unregisterAction(currentUrl);
                        }
                        return reject(responseData);
                    }))
                        .catch(() => {
                        if (isLocalCacheEnabled) {
                            this.cacheInstance.unregisterAction(currentUrl);
                        }
                        return reject();
                    });
                };
                if (hasReCaptcha && useRecaptcha) {
                    runGoogleReCaptcha(token, fetchCall);
                }
                else {
                    fetchCall();
                }
            };
            const handleError = (error) => {
                var _a, _b;
                const isCustomError = isCustomErrorCheck(error);
                const message = isCustomError ? (_a = error.message) !== null && _a !== void 0 ? _a : `${this.name}ConnectionError` : `${this.name}ConnectionError`;
                const statusCode = isCustomError ? (_b = error.statusCode) !== null && _b !== void 0 ? _b : 0 : 0;
                newrelic.noticeError(new ConnectionError(message, this.baseUrl, url, statusCode), {
                    response: JSON.stringify(error),
                    url,
                    baseUrl: this.baseUrl,
                });
                if (isCustomError && this.isEvo) {
                    error.message = __("evo.fallback.error");
                    throw error;
                }
                throw error;
            };
            if (isLocalCacheEnabled) {
                if (this.cacheInstance.hasValue(currentUrl)) {
                    return yield this.cacheInstance.getValue(currentUrl, promiseCallback).catch(handleError);
                }
                this.cacheInstance.registerAction(currentUrl);
            }
            return new Promise(promiseCallback).catch(handleError);
        });
    }
    initLocale() {
        var _a;
        if (!Rest.locale && typeof document !== "undefined") {
            Rest.locale = ((_a = document.querySelector("html")) === null || _a === void 0 ? void 0 : _a.getAttribute("lang")) || undefined;
        }
        if (Rest.locale) {
            this.setCommonHeader("x-des-locale", Rest.locale);
        }
    }
}
//# sourceMappingURL=Rest.js.map